import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AcademicRefBox } from './AcademicRefBox';
import { PAGE_TEXT } from '../lang';
import {
  type AcademicRefFormData,
  useGetAcademicReferee,
  useUpdateAcademicReferee,
  addApplicationItem,
} from '../../../graphql';
import {
  Loading,
  Alert,
  type AlertDetails,
  Button,
} from '../../../../../components';

type AcademicRefContentProps = {
  index: number;
  academicId?: string;
  enableEdit: boolean;
  enableDelete: boolean;
  handleDelete: () => void;
  refetchApplication: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

export const AcademicRefContent: React.FC<AcademicRefContentProps> = (
  props: AcademicRefContentProps,
) => {
  const { applicationId: id } = useParams();
  const {
    index,
    academicId: initialAcademicRefId = '',
    enableEdit,
    refetchApplication,
    enableDelete,
    handleDelete,
    handleLoading,
  } = props;

  let alertDetailDef: AlertDetails = {
    alertContent: '',
    alertType: 'error',
  };
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] =
    useState<AlertDetails>(alertDetailDef);

  const [academicId, setAcademicId] = useState(initialAcademicRefId);

  const isCreateMode = academicId === '';
  let initialAcademicRefValues: AcademicRefFormData = {
    title: '',
    surname: '',
    givenName: '',
    position: '',
    institution: '',
    relationship: '',
    email: '',
    phoneNumber: '',
    faxNumber: '',
  };

  const { academicRefValue, loading, error } =
    useGetAcademicReferee(academicId);

  if (academicRefValue.name !== undefined) {
    const { name, ...others } = academicRefValue;
    initialAcademicRefValues = {
      surname: name.surname,
      givenName: name.givenName,
      ...others,
    };
  }

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const updateAcademicReferee = useUpdateAcademicReferee();
  const createAcademicReferee = addApplicationItem(
    'academicReferees',
    refetchApplication,
  );

  const handleSubmit = async (values: AcademicRefFormData): Promise<void> => {
    await handleLoading(async () => {
      try {
        const { surname, givenName, ...others } = values;
        if (isCreateMode) {
          const res = await createAcademicReferee({
            variables: {
              id,
              input: {
                academicReferee: {
                  name: {
                    surname,
                    givenName,
                  },
                  ...others,
                },
              },
            },
          });
          const resAcademicId =
            res.data?.addApplicationItem.academicReferees[index]._id;
          setAcademicId(resAcademicId);
        } else {
          await updateAcademicReferee({
            variables: {
              id: academicId,
              ...values,
            },
          });
        }
        alertDetailDef = {
          alertContent: 'Saved Academic Referee Successfully.',
          alertType: 'success',
        };
      } catch (e) {
        alertDetailDef = {
          alertContent: `Saved Academic Referee Failed.${(e as Error)?.message}`,
          alertType: 'error',
        };
      } finally {
        setAlertDetails(alertDetailDef);
      }

      setOpenAlert(true);
    });
  };

  const academicBoxData = {
    initialAcademicRefValues,
    enableEdit,
    academicRefIndex: index,
    actionArea: (
      <div className="buttons-area">
        <Button variant="contained" type="submit">
          {PAGE_TEXT.SAVE}
        </Button>
        {enableDelete && (
          <Button onClick={handleDelete} variant="outlined">
            {PAGE_TEXT.DELETE}
          </Button>
        )}
      </div>
    ),
    handleSubmit,
  };

  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {loading ? (
        <Loading />
      ) : (
        <div className="mt-2 mb-4">
          <AcademicRefBox {...academicBoxData} />
        </div>
      )}
    </>
  );
};
